import { useCallback, useEffect, useState } from "react";
import { UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime, fmtDateLong } from "@/lib/format";
import { PageHeader, Panel, StatusBadge } from "@/components/common/ui-kit";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserCreateDialog } from "@/pages/admin/UserCreateDialog";
import { UserStatusAction } from "@/pages/admin/UserStatusAction";
import { UserAccessAction } from "@/pages/admin/UserAccessAction";

const RoleCell = ({ u, roles, editable, onChange }) => {
  if (!editable) return <span className="text-sm text-slate-700">{u.role_name}</span>;
  return (
    <Select value={u.role_key} onValueChange={(v) => onChange(u, v)}>
      <SelectTrigger className="h-8 w-48 text-sm" data-testid={`user-role-select-${u.id}`}><SelectValue /></SelectTrigger>
      <SelectContent>{roles.map((r) => <SelectItem key={r.key} value={r.key}>{r.name}</SelectItem>)}</SelectContent>
    </Select>
  );
};

export default function AdminUsers() {
  const { me, can } = useAuth();
  const manage = can("users:manage");
  const [users, setUsers] = useState(null);
  const [roles, setRoles] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(() => api.get("/admin/users").then((r) => setUsers(r.data.items)).catch((e) => toast.error(errorMessage(e))), []);
  useEffect(() => {
    load();
    if (can("roles:read")) api.get("/admin/roles").then((r) => setRoles(r.data.roles)).catch(() => {});
  }, [load, can]);

  const changeRole = async (u, role_key) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { role_key });
      toast.success("Ruolo aggiornato");
      load();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  return (
    <div data-testid="admin-users-page">
      <PageHeader overline="Amministrazione" title="Utenti"
        description="Utenti autorizzati ad accedere tramite Microsoft. Disabilitare un utente ne blocca l'accesso senza cancellarne lo storico."
        actions={manage && <Button onClick={() => setCreateOpen(true)} className="gap-2" data-testid="admin-users-add-button"><UserPlus className="h-4 w-4" />Autorizza utente</Button>} />
      <Panel className="overflow-x-auto">
        <Table data-testid="admin-users-data-table">
          <TableHeader>
            <TableRow>
              <TableHead>Utente</TableHead><TableHead>Ruolo</TableHead><TableHead>Identità Microsoft</TableHead>
              <TableHead>Stato</TableHead><TableHead>Ultimo accesso</TableHead><TableHead>Creato</TableHead><TableHead className="text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(users || []).map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell>
                  <p className="text-sm font-medium text-slate-800">{`${u.first_name} ${u.last_name}`.trim() || "—"}{u.is_demo && <span className="ml-2 text-[10px] font-semibold uppercase text-amber-600">Demo</span>}</p>
                  <p className="text-xs text-slate-500">{u.email}</p>
                </TableCell>
                <TableCell><RoleCell u={u} roles={roles} editable={manage && roles.length > 0 && u.id !== me.user.id} onChange={changeRole} /></TableCell>
                <TableCell>{u.microsoft_linked ? <span className="font-mono text-xs text-slate-600">{u.entra_oid_hint}</span> : <span className="text-xs text-slate-400">Non ancora collegata</span>}</TableCell>
                <TableCell>
                  <StatusBadge tone={u.status === "active" ? "green" : "neutral"} data-testid={`user-status-${u.id}`}>{u.status === "active" ? "Attivo" : "Disabilitato"}</StatusBadge>
                  {u.disabled_at && <p className="mt-1 text-[11px] text-slate-400">dal {fmtDateLong(u.disabled_at)}</p>}
                </TableCell>
                <TableCell className="text-xs text-slate-600">{u.last_login_at ? fmtDateTime(u.last_login_at) : "Mai"}</TableCell>
                <TableCell className="text-xs text-slate-600">{fmtDateLong(u.created_at)}</TableCell>
                <TableCell className="text-right">{manage && <UserAccessAction user={u} rolePerms={roles.find((r) => r.key === u.role_key)?.permissions || []} onDone={load} />}{manage && u.id !== me.user.id && <UserStatusAction user={u} onDone={load} />}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {users === null && <p className="p-6 text-sm text-slate-500">Caricamento…</p>}
      </Panel>
      {manage && <UserCreateDialog open={createOpen} onOpenChange={setCreateOpen} roles={roles} onCreated={load} />}
    </div>
  );
}
